import { useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { getBusinessById, uploadBusinessImages, deleteBusinessImage } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { ConfirmModal, useConfirm } from '../../components/ui/ConfirmModal'

export default function BusinessImages() {
  const { id } = useParams()
  const { confirm, close, state: confirmState } = useConfirm()
  const qc = useQueryClient()

  const { data: business, isLoading } = useQuery({
    queryKey: ['business', id],
    queryFn: () => getBusinessById(id),
  })

  const uploadMut = useMutation({
    mutationFn: (files) => uploadBusinessImages(id, files),
    onSuccess: () => { qc.invalidateQueries(['business', id]); toast.success('Uploaded') },
    onError: (e) => toast.error(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (imageId) => deleteBusinessImage(id, imageId),
    onSuccess: () => { qc.invalidateQueries(['business', id]); toast.success('Deleted') },
    onError: (e) => toast.error(e.message),
  })

  const onDrop = useCallback((files) => uploadMut.mutate(files), [uploadMut])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'image/*': [] }, multiple: true })

  if (isLoading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/businesses"><Button variant="ghost" size="sm"><i className="ti ti-arrow-left" /></Button></Link>
        <h1 className="text-xl font-bold text-gray-900">Images — {business?.name}</h1>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors mb-6 ${isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'}`}
      >
        <input {...getInputProps()} />
        {uploadMut.isPending ? (
          <div className="flex justify-center"><Spinner /></div>
        ) : (
          <>
            <i className="ti ti-upload text-3xl text-gray-400 mb-2 block" />
            <p className="text-sm text-gray-500">{isDragActive ? 'Drop files here' : 'Drag & drop images or click to browse'}</p>
          </>
        )}
      </div>

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      {!business?.images?.length && (
        <div className="text-center py-10 text-gray-400">
          <i className="ti ti-photo-off text-3xl mb-2 block" />
          <p className="text-sm">No images yet. Upload your first image above.</p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4">
        {business?.images?.map(img => (
          <div key={img.id} className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square">
            <img src={img.url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button
                variant="danger"
                size="sm"
                onClick={() => confirm({ title: 'Delete Image', message: 'Delete this image? This action cannot be undone.', onConfirm: () => deleteMut.mutate(img.id) })}
              >
                <i className="ti ti-trash" />
              </Button>
            </div>
            {img.order === 0 && (
              <span className="absolute top-1 left-1 bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded">Main</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
